import {
  ApiTags,
  LanguageNameSpaces,
  PrecisionType,
  TimeRange,
  TradeData,
} from "@shared/types";
import { selectDisplayCurrency } from "@shared/slices/common.slice";
import { useDispatch, useSelector } from "react-redux";
import { DISPLAY_CURRENCIES, TIME_RANGE_1_DAY } from "@shared/common/constants";
import { sharedLib } from "@shared/services/shared.service";
import { usePrecisions } from "@shared/utils/hooks/usePrecisions";
import { avoidScientificNumber, toFixed } from "@shared/utils/numbers";
import { dashboardSelectors } from "@shared/slices/dashboard.slice";
import { useEffect, useMemo, useState } from "react";
import { MOBILE_GET_OPEN_TRADES_ARGS } from "@shared/features/trades/common/constants";
import { useTheme } from "styled-components";
import { dashboardFilters } from "@shared/slices/filters.slice";
import useGraphDataSocket from "@shared/services/socket/useGraphDataSocket";
import usePricesSocket from "@shared/services/socket/usePricesSocket";
import { formatTimeSeriesChartData } from "@shared/utils/charts";
import { useTradesSort } from "@shared/features/trades/useTradesSort";
import useSocketAggTradeData from "@shared/features/trades/common/useSocketAggTradeData";
import { skipToken } from "@reduxjs/toolkit/query";
import {
  COIN_TO_BTC_PRECISION,
  COIN_TO_USD_PRECISION,
} from "@shared/features/dashboard/constants";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";

interface UseSharedDashboardProps {
  selectedTimeRange: TimeRange;
  withPriceGraphs?: boolean;
}

export const useSharedDashboard = ({
  selectedTimeRange,
  withPriceGraphs,
}: UseSharedDashboardProps) => {
  const dispatch = useDispatch();
  const getAmountsHistoryStatus =
    sharedLib.sharedApi.portfolio.useGetAmountsHistoryQuery({
      time_range: selectedTimeRange,
    });
  const getDashboardDataStatus =
    sharedLib.sharedApi.dashboard.useGetDashboardDataQuery();
  const getOpenTradesStatus =
    sharedLib.sharedApi.trades.useGetOpenTradesDataQuery(
      withPriceGraphs ? MOBILE_GET_OPEN_TRADES_ARGS : undefined
    );
  const amountsHistoryData = getAmountsHistoryStatus.data;

  useEffect(() => {
    if (amountsHistoryData) {
      dispatch(
        dashboardFilters.actions.loadFilters({ data: amountsHistoryData })
      );
    }
  }, [amountsHistoryData]);

  const refetchDashboardData = () => {
    dispatch(
      sharedLib.sharedApi.common.util.invalidateTags([
        { type: ApiTags.PRICE_CHARTS, id: "LIST" },
      ])
    );
    try {
      getDashboardDataStatus.refetch();
      getAmountsHistoryStatus.refetch();
      getOpenTradesStatus.refetch();
    } catch {}
  };

  const isLoading =
    getAmountsHistoryStatus.isLoading ||
    getDashboardDataStatus.isLoading ||
    getOpenTradesStatus.isLoading;

  const isFetching =
    getAmountsHistoryStatus.isFetching ||
    getDashboardDataStatus.isFetching ||
    getOpenTradesStatus.isFetching;

  const balanceFetching = getAmountsHistoryStatus.isFetching;

  return {
    getDashboardDataStatus,
    getAmountsHistoryStatus,
    getOpenTradesStatus,
    refetchDashboardData,
    isLoading,
    isFetching,
    amountsHistoryData,
    balanceFetching,
  };
};

interface UseDashboardBalanceProps {
  selectedTimeRange: TimeRange;
}

export const useDashboardBalance = ({
  selectedTimeRange,
}: UseDashboardBalanceProps) => {
  const theme = useTheme();
  const displayCurrency = useSelector(selectDisplayCurrency);
  const [expanded, setExpanded] = useState(false);

  const graphDataSelector = useMemo(
    () => dashboardSelectors.selectAccountChartData(selectedTimeRange),
    [selectedTimeRange]
  );
  const graphData = useSelector(graphDataSelector);
  const { getDisplayCurrencyAmountPrecision } = usePrecisions();
  const balancePrecision = getDisplayCurrencyAmountPrecision();

  const balanceValues = useMemo(() => {
    let balance = 0;
    let change = 0;
    let percentChange = 0;
    if (graphData.length) {
      balance = graphData[graphData.length - 1].y;
      const initialBalance = graphData[0].y;
      change = balance - initialBalance;
      percentChange = initialBalance ? change / initialBalance : 0;
    }
    return {
      balance: toFixed(balance, balancePrecision),
      change: toFixed(change, balancePrecision),
      percentChange: toFixed(percentChange, 4),
    };
  }, [graphData, balancePrecision]);

  return {
    theme,
    displayCurrency,
    graphData,
    balanceValues,
    expanded,
    setExpanded,
    selectedTimeRange,
    balancePrecision,
  };
};

export const useDashboardStatistics = (
  useFilteredTrades?: boolean,
  withPriceGraphs?: boolean
) => {
  const theme = useTheme();
  const displayCurrency = useSelector(selectDisplayCurrency);
  const { getDisplayCurrencyAmountPrecision } = usePrecisions();
  const precision = getDisplayCurrencyAmountPrecision();

  const args = withPriceGraphs ? MOBILE_GET_OPEN_TRADES_ARGS : undefined;
  const { data, isLoading, isFetching } =
    sharedLib.sharedApi.trades.useGetOpenTradesDataQuery(args);

  const getTradesSelector = useMemo(
    () => dashboardSelectors.selectOpenTrades(args),
    [args]
  );

  const filteredTrades = useSelector(getTradesSelector);
  const openTradesData =
    (useFilteredTrades ? filteredTrades : data?.data) || [];

  const getSumOfTradesStatistic = (
    usdValue: keyof Pick<
      TradeData,
      "usd_amount_change" | "allocated_amount_usd" | "potential_profit_usd"
    >,
    btcValue: keyof Pick<
      TradeData,
      "btc_amount_change" | "allocated_amount_btc" | "potential_profit_btc"
    >
  ) => {
    const valueByCurrency =
      displayCurrency === DISPLAY_CURRENCIES.USD ? usdValue : btcValue;
    return toFixed(
      openTradesData.reduce(
        (sumOfStatistic, trade) => sumOfStatistic + trade[valueByCurrency],
        0
      ),
      precision
    );
  };

  const { t } = useTypedTranslation(LanguageNameSpaces.DASHBOARD);
  const helperProps = {
    title: t("tooltips.amount_on_trades_title"),
    description: t("tooltips.amount_on_trades_description"),
    analyticsFieldName: "Amount in trades",
    analyticsPageName: "Dashboard",
    iconColor: theme.colors.secondaryGrey,
    iconStyle: { color: theme.colors.secondaryGrey },
  };

  return {
    numTrades: openTradesData.length,
    tradesAmount: getSumOfTradesStatistic(
      "allocated_amount_usd",
      "allocated_amount_btc"
    ),
    tradesPnl: getSumOfTradesStatistic(
      "usd_amount_change",
      "btc_amount_change"
    ),
    tradesPotentialProfit: getSumOfTradesStatistic(
      "potential_profit_usd",
      "potential_profit_btc"
    ),
    isLoading,
    isFetching,
    openTradesData,
    precision,
    helperProps,
  };
};

export const useDashboardTopCoins = () => {
  const displayCurrency = useSelector(selectDisplayCurrency);
  const isUsdCurrency = displayCurrency === DISPLAY_CURRENCIES.USD;

  const [selectedIndex, setSelectedIndex] = useState(0);

  const onChangeSelectedIndex = (index: number) => {
    setSelectedIndex(index);
  };

  const getDashboardDataStatus =
    sharedLib.sharedApi.dashboard.useGetDashboardDataQuery();
  const assetSummary = getDashboardDataStatus.data?.asset_summary;
  const fulfilledTimeStamp = getDashboardDataStatus.fulfilledTimeStamp;

  const getSymbolPricePrecision = (symbol: string) =>
    isUsdCurrency
      ? COIN_TO_USD_PRECISION[symbol]
      : COIN_TO_BTC_PRECISION[symbol];

  return {
    isUsdCurrency,
    displayCurrency,
    selectedIndex,
    onChangeSelectedIndex,
    getSymbolPricePrecision,
    assetSummary,
    fulfilledTimeStamp,
  };
};

interface UseDashboardTradeCardsProps {
  isFocused?: boolean;
}

export const useDashboardTradeCards = ({
  isFocused,
}: UseDashboardTradeCardsProps) => {
  const getOpenTradesStatus =
    sharedLib.sharedApi.trades.useGetOpenTradesDataQuery(
      MOBILE_GET_OPEN_TRADES_ARGS
    );
  const tradesSelector = useMemo(
    () => dashboardSelectors.selectOpenTrades(MOBILE_GET_OPEN_TRADES_ARGS),
    []
  );
  const filtersApplied =
    useSelector(dashboardFilters.selectors.selectNumActiveFilters) > 0;
  const trades = useSelector(tradesSelector);
  const { sortedData } = useTradesSort(trades, true);
  const rawTradesData = getOpenTradesStatus.data?.data;

  const socketArgs = useMemo(
    () => ({ fetch_initial_data: !!getOpenTradesStatus.data, as_dict: true }),
    [isFocused]
  );
  useSocketAggTradeData(
    socketArgs,
    MOBILE_GET_OPEN_TRADES_ARGS,
    skipToken,
    undefined,
    getOpenTradesStatus.refetch
  );

  const numOfHotTrades = useMemo(
    () => sortedData?.filter((trade) => trade.closest_order?.is_hot).length,
    [sortedData]
  );

  return {
    rawTradesData,
    sortedData,
    numOfHotTrades,
    getOpenTradesStatus,
    filtersApplied,
  };
};

export const useMobileTradePriceChart = (tradeData: TradeData) => {
  const { precision } = usePrecisions({
    symbol: tradeData.symbol,
    exchange: tradeData.exchange,
    precisionType: PrecisionType.PRICE,
  });
  const { graphData: socketGraphData, timestamp: graphTimestamp } =
    useGraphDataSocket(tradeData.symbol, tradeData.exchange);
  const graphData = (socketGraphData ?? tradeData.price_graph_data)?.[
    TIME_RANGE_1_DAY
  ];

  const { socketPrice: rawSocketPrice, timestamp: priceTimestamp } =
    usePricesSocket(tradeData.symbol, tradeData.exchange);
  let socketPrice: number | undefined;
  let currentPrice = graphData?.length
    ? graphData[graphData.length - 1][1]
    : undefined;
  if (rawSocketPrice.value && priceTimestamp > graphTimestamp) {
    socketPrice = rawSocketPrice.value;
  }
  if (socketPrice) currentPrice = socketPrice;

  const formattedCurrentPrice = useMemo(() => {
    return currentPrice ? avoidScientificNumber(currentPrice) : undefined;
  }, [currentPrice]);

  const formattedSocketPrice = useMemo(() => {
    return socketPrice ? avoidScientificNumber(socketPrice) : undefined;
  }, [socketPrice]);

  const formattedGraphData = useMemo(() => {
    return formatTimeSeriesChartData(
      graphData as any,
      1,
      true,
      null,
      precision
    );
  }, [graphData, precision]);

  return {
    graphData,
    precision,
    currentPrice: formattedCurrentPrice,
    socketPrice: formattedSocketPrice,
    formattedGraphData,
  };
};
